import { getAuthToken } from "jwt-storage-lib";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Build, BuildTypes, GlobalBlockTypes } from "@common/build/types";
import { setBuild } from "../BuilderSlice/builderSlice";
import { RawBuild } from "../../../types/build.type";
import { convertRawBuildToBuild } from "../BuilderSlice/builderHelpers";
import { updatePaymentGrid } from "../buildSettings/updatePaymentGrid";

export interface GBResponse {
    id: string;
    name: string;
    img: string;
    sourceId?: string;
}

export type PublishDetails = {
    version: string;
    changelog: string;
    updateSettings: {
        notifyUser: boolean;
        autoUpdate: boolean;
        scheduleTime: string | undefined;
    };
};

export type GetBuildByCompanyIdParams = {
    limit?: number;
    offset?: number;
    isPrintBlocks?: boolean;
    isPublished?: boolean;
};

const convertBuildToPayload = (build: Partial<Build>) => {
    const { id, pages, popups, cfds, actionBuilder, globalBlocks, ...rest } = build;
    return {
        ...rest,
        tag: build.tag,
        _id: id,
        pages: JSON.stringify(build.pages || []),
        popups: JSON.stringify(build.popups || []),
        cfds: JSON.stringify(build.cfds || []),
        actionBuilder: JSON.stringify(build.actionBuilder || {}),
        globalBlocks: JSON.stringify(build.globalBlocks || {})
    };
};

export const buildsApi = createApi({
    reducerPath: "buildsApi",
    baseQuery: fetchBaseQuery({
        baseUrl: `${import.meta.env.VITE_HUB_API}/builds`,
        prepareHeaders: async headers => {
            const token = await getAuthToken();

            if (token) {
                headers.set("Authorization", `Bearer ${token}`);
            }

            return headers;
        }
    }),
    tagTypes: ["builds", "latestBuilds", "globalBlocks"],
    endpoints: builder => ({
        getBuild: builder.query<RawBuild, { companyId: string; buildId: string }>({
            query: ({ companyId, buildId }) => `/${companyId}/${buildId}`,
            providesTags: ["builds"],
            async onQueryStarted(_, { queryFulfilled, dispatch }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(setBuild(data));
                } catch (error) {
                    console.error("Error getting build", error);
                }
            }
        }),
        getBuildByCompanyId: builder.query<
            Build[],
            {
                companyId: string;
                params?: GetBuildByCompanyIdParams;
            }
        >({
            query: ({ companyId, params }) => ({
                url: `/query/${companyId}`,
                params: {
                    ...(params || {})
                }
            }),
            transformResponse: (response: RawBuild[]) => {
                return response.map(build => convertRawBuildToBuild(build));
            },
            providesTags: ["builds"]
        }),
        //builds/query/${companyId}?limit=120&offset=0&isPrintBlocks=false
        getLatestBuilds: builder.query<Build[], string>({
            query: companyId => `${import.meta.env.VITE_HUB_API}/builds/query/${companyId}?limit=120&offset=0&isPrintBlocks=false`,
            transformResponse: (response: RawBuild[]) => {
                return response.map(build => convertRawBuildToBuild(build));
            },
            providesTags: ["latestBuilds"]
        }),
        saveBuild: builder.mutation<Build, { companyId: string; build: Partial<Build> }>({
            query: ({ companyId, build }) => ({
                url: `/builder/save-build/${companyId}`,
                method: "PATCH",
                body: convertBuildToPayload(build)
            }),
            invalidatesTags: ["builds"],
            async onQueryStarted(queryPayload) {
                try {
                    const { companyId, build } = queryPayload;

                    await updatePaymentGrid(build, companyId);
                } catch (error) {
                    console.error("Error setting payment grid in payment settings", error);
                }
            }
        }),
        publishBuild: builder.mutation<Build, { companyId: string; build: Partial<Build>; publishDetails?: PublishDetails }>({
            query: ({ companyId, build, publishDetails }) => ({
                url: `/builder/publish-build/${companyId}`,
                method: "PATCH",
                body: { ...convertBuildToPayload(build), ...publishDetails }
            }),
            invalidatesTags: ["builds"],
            async onQueryStarted(queryPayload) {
                try {
                    const { companyId, build } = queryPayload;

                    await updatePaymentGrid(build, companyId);
                } catch (error) {
                    console.error("Error setting payment grid in payment settings", error);
                }
            }
        }),
        getGlobalBlocks: builder.query<GBResponse[], { companyId: string; gbType: GlobalBlockTypes }>({
            query: ({ companyId, gbType }) => `/builder/get-global-blocks/${companyId}?gbType=${gbType}`,
            providesTags: ["globalBlocks"]
        }),
        getHighestPublishedVersion: builder.query<[number, number, number], { companyId: string; buildId: string }>({
            query: ({ companyId, buildId }) => `/builder/highest-published-version/${companyId}/${buildId}`
        }),
        createGlobalBlock: builder.mutation<string, { companyId: string; gbType: GlobalBlockTypes; name: string }>({
            query: ({ companyId, gbType, name }) => ({
                url: `/${companyId}`,
                method: "POST",
                body: {
                    companyId,
                    buildType: BuildTypes.GLOBAL_BLOCK,
                    globalBlockType: gbType,
                    isPublished: false,
                    name
                }
            })
        })
    })
});

export const {
    useLazyGetBuildQuery,
    useSaveBuildMutation,
    usePublishBuildMutation,
    useGetLatestBuildsQuery,
    useLazyGetLatestBuildsQuery,
    useGetGlobalBlocksQuery,
    useGetHighestPublishedVersionQuery,
    useCreateGlobalBlockMutation,
    useGetBuildByCompanyIdQuery,
    useLazyGetBuildByCompanyIdQuery
} = buildsApi;
